import { Navigate } from "react-router-dom";
import { useAuth, type AppRole } from "@/hooks/useAuth";
import { Skeleton } from "@/components/ui/skeleton";

type Props = {
  allowedRoles: AppRole[];
  children: React.ReactNode;
};

/** Returns the default route a user should land on based on their roles */
export function getDefaultRouteForRoles(roles: AppRole[]): string {
  if (roles.includes("admin") || roles.includes("gerencia") || roles.includes("recepcion") || roles.includes("rrss")) {
    return "/admin";
  }
  if (roles.includes("encuesta")) return "/admin/hotel?tab=surveys";
  if (roles.includes("meseros") || roles.includes("cocina")) return "/admin";
  return "/admin/mi-cuenta";
}

function RoleGuardSkeleton() {
  return (
    <div className="p-6 space-y-6">
      <Skeleton className="h-8 w-48" />
      <Skeleton className="h-4 w-72" />
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-4">
        <Skeleton className="h-32 rounded-lg" />
        <Skeleton className="h-32 rounded-lg" />
        <Skeleton className="h-32 rounded-lg" />
      </div>
      <Skeleton className="h-64 rounded-lg" />
    </div>
  );
}

export default function RoleGuard({ allowedRoles, children }: Props) {
  const { roles, isLoading } = useAuth();

  if (isLoading) return <RoleGuardSkeleton />;

  const hasAccess = roles.some((r) => allowedRoles.includes(r));

  if (!hasAccess) {
    const fallback = getDefaultRouteForRoles(roles);
    return <Navigate to={fallback} replace />;
  }

  return <>{children}</>;
}
